import { useMutation, useQueryClient } from '@tanstack/react-query';

import { STORAGE_KEYS } from '@/constants';
import {
  getOTPAsync,
  verifyOTPAsync,
  signUp,
  updateUserProfile,
} from '@/service/AuthenticationService';
import { disconnectSignalR } from '@/service/signalRService';
import { useAppDispatch, clearAuth, resetStartup } from '@/store';

import type { VerifyOTPResponse } from '@/service/types/authenticatetypes';
import type {
  SignUpRequestDTO,
  SignUpResponseDTO,
  UpdateUserProfileRequestDTO,
  UpdateUserProfileResponseDTO,
} from '@/service/types/usertypes';

// ─────────────────────────────────────────────────────────────────
// Mutation Hooks
// ─────────────────────────────────────────────────────────────────

interface SendOTPParams {
  countryCode: string;
  mobile: string;
}

export const useSendOTPMutation = () => {
  return useMutation({
    mutationFn: async ({
      countryCode,
      mobile,
    }: SendOTPParams): Promise<{ message: string; registered: boolean }> => {
      return getOTPAsync(countryCode, mobile);
    },
  });
};

interface VerifyOTPParams {
  countryCode: string;
  mobile: string;
  otp: string;
  latitude: number;
  longitude: number;
  deviceId: string;
}

export const useVerifyOTPMutation = () => {
  return useMutation({
    mutationFn: async ({
      countryCode,
      mobile,
      otp,
      latitude,
      longitude,
      deviceId,
    }: VerifyOTPParams): Promise<{ result: VerifyOTPResponse; accessToken: string }> => {
      return verifyOTPAsync(countryCode, mobile, otp, latitude, longitude, deviceId);
    },
  });
};

export const useSignUpMutation = () => {
  return useMutation({
    mutationFn: async (request: SignUpRequestDTO): Promise<SignUpResponseDTO> => {
      return signUp(request);
    },
  });
};

export const useUpdateProfileMutation = () => {
  return useMutation({
    mutationFn: async (
      request: UpdateUserProfileRequestDTO
    ): Promise<UpdateUserProfileResponseDTO> => {
      return updateUserProfile(request);
    },
  });
};

export const useLogoutMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: (): Promise<boolean> => {
      disconnectSignalR();
      localStorage.removeItem(STORAGE_KEYS.user);
      localStorage.removeItem(STORAGE_KEYS.sessionId);
      localStorage.removeItem(STORAGE_KEYS.authToken);
      localStorage.removeItem(STORAGE_KEYS.tokenExpiry);
      localStorage.removeItem(STORAGE_KEYS.plant);
      localStorage.removeItem('yms_verify_data');
      sessionStorage.removeItem(STORAGE_KEYS.refreshToken);
      sessionStorage.removeItem(STORAGE_KEYS.otpMobile);
      sessionStorage.removeItem(STORAGE_KEYS.otpCountryCode);
      localStorage.removeItem(STORAGE_KEYS.breakModeActive);
      localStorage.removeItem(STORAGE_KEYS.breakModeFrom);
      localStorage.removeItem(STORAGE_KEYS.breakTimerStart);
      localStorage.removeItem(STORAGE_KEYS.shuttleModeActive);
      localStorage.removeItem(STORAGE_KEYS.shuttleTimerStart);
      localStorage.removeItem(STORAGE_KEYS.shuttleTimerPausedElapsed);
      localStorage.removeItem(STORAGE_KEYS.offlineUpdatedTaskList);
      localStorage.removeItem(STORAGE_KEYS.offlineMyTaskList);
      localStorage.removeItem(STORAGE_KEYS.inProgressTask);
      localStorage.removeItem(STORAGE_KEYS.offlineUpcomingTask);
      localStorage.removeItem(STORAGE_KEYS.offlineCompletedTask);
      localStorage.removeItem(STORAGE_KEYS.loginDateTime);
      localStorage.removeItem(STORAGE_KEYS.lastActivityTime);
      return Promise.resolve(true);
    },
    onSuccess: () => {
      void queryClient.cancelQueries();
      queryClient.clear();
      dispatch(clearAuth());
      dispatch(resetStartup());
    },
  });
};
